import { maxQuantity } from "./cart.ts";
import type { CartProductSnapshot, Product } from "./types";

export type ProductCardProduct = Pick<Product,
  "id" | "slug" | "name" | "subtitle" | "material" | "images" | "basePrice" | "oldPrice" | "demo" | "created_at"
> & {
  cardPrice?: number;
  hasMadeToOrder: boolean;
  swatches: Array<Pick<Product["variants"][number], "id" | "hex" | "color">>;
  quickAddData: CartProductSnapshot;
};

/**
 * Cards only need enough variant data to decide between one-click add and
 * opening the product page. Keep the selected purchasable variant intact for
 * one-click add, and use two entries as a sentinel when the product has
 * multiple choices. This avoids serializing every size/color into homepage
 * client props while preserving cart behavior.
 */
export function quickAddSnapshot(product: Product): CartProductSnapshot {
  const purchasableVariants = product.variants.filter((variant) => maxQuantity(variant) > 0);
  const variants = purchasableVariants.length === 1
    ? purchasableVariants
    : purchasableVariants.slice(0, 2);

  return {
    id: product.id,
    slug: product.slug,
    name: product.name,
    subtitle: product.subtitle,
    images: product.images.slice(0, 2),
    variants,
    basePrice: product.basePrice,
    oldPrice: product.oldPrice,
    demo: product.demo,
    purchasable: product.purchasable !== false,
  };
}

export function productCardData(product: Product): ProductCardProduct {
  const prices = product.variants.map((variant) => variant.price).filter((price) => Number.isFinite(price) && price > 0);
  return {
    id: product.id, slug: product.slug, name: product.name, subtitle: product.subtitle,
    material: product.material, images: product.images.slice(0, 2), created_at: product.created_at,
    cardPrice: prices.length ? Math.min(...prices) : product.basePrice,
    hasMadeToOrder: product.variants.some((variant) => variant.made_to_order),
    swatches: product.variants.filter((variant) => variant.hex).map(({ id, hex, color }) => ({ id, hex, color })),
    quickAddData: quickAddSnapshot(product),
    basePrice: product.basePrice, oldPrice: product.oldPrice, demo: product.demo,
  };
}
